import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  FormControlLabel,
  Checkbox,
  Alert,
  AlertTitle,
  Collapse,
  Stack,
  Divider,
} from '@mui/material'

import { useAuth } from '../../contexts/AuthContext'
import { useNotification } from '../../contexts/NotificationContext'
import PasswordField from '../../components/PasswordField'
import { trackCustomRegistrationEvent } from '../../utils/adsLeadAttributionUtils'

const Signup: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { signup } = useAuth()
  const { showNotification } = useNotification()

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // ── Validators ────────────────────────────────────────────────────────────
  const validators: Record<string, (val: string, all?: typeof formData) => string> = {
    name: (v) => {
      if (!v.trim()) return 'Full name is required'
      if (v.trim().length > 255) return 'Name must not exceed 255 characters'
      return ''
    },
    email: (v) => {
      if (!v.trim()) return 'Email address is required'
      // RFC 5322-inspired regex — catches most invalid emails
      if (!/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/.test(v.trim())) {
        return 'Please enter a valid email address'
      }
      return ''
    },
    password: (v) => {
      if (!v) return 'Password is required'
      if (v.length < 8) return 'Password must be at least 8 characters'
      if (!/[A-Z]/.test(v)) return 'Include at least one uppercase letter (A–Z)'
      if (!/[a-z]/.test(v)) return 'Include at least one lowercase letter (a–z)'
      if (!/[0-9]/.test(v)) return 'Include at least one number (0–9)'
      if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(v)) return 'Include at least one special character (!@#$%...)'
      return ''
    },
    confirmPassword: (v, all) => {
      if (!v) return 'Please confirm your password'
      if (all && v !== all.password) return 'Passwords do not match'
      return ''
    },
  }

  const validateField = (name: string, value: string) => {
    const fn = validators[name]
    return fn ? fn(value, formData) : ''
  }

  const validateAll = () => {
    const errors: Record<string, string> = {}
    ;(['name', 'email', 'password', 'confirmPassword'] as const).forEach((field) => {
      const err = validateField(field, formData[field] as string)
      if (err) errors[field] = err
    })
    return errors
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target
    const newVal = name === 'agreeToTerms' ? checked : value
    setFormData(prev => ({ ...prev, [name]: newVal }))
    // Clear the field error as user types so they see progress
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const errorDismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showError = (msg: string) => {
    setError(msg)
    if (errorDismissTimer.current) clearTimeout(errorDismissTimer.current)
    errorDismissTimer.current = setTimeout(() => setError(''), 8000)
  }

  useEffect(() => () => {
    if (errorDismissTimer.current) clearTimeout(errorDismissTimer.current)
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const errors = validateAll()
    setFieldErrors(errors)
    if (Object.keys(errors).length > 0) return

    if (!formData.agreeToTerms) {
      showError('Please agree to the Terms of Service and Privacy Policy')
      return
    }

    setIsLoading(true)
    try {
      await signup(formData.email.trim(), formData.password, formData.name.trim())
      trackCustomRegistrationEvent()
      showNotification('Account created successfully!', 'success')
      navigate('/dashboard' + location.search)
    } catch (err: any) {
      console.error('[Signup]', err)
      const fallbackMsg = 'Something went wrong while creating your account. Please try again.'
      const msg = err?.status === 500
        ? fallbackMsg
        : (err?.message || fallbackMsg)
      showError(msg)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box
      sx={{
        width: '100%',
        // Mobile: natural scroll. Desktop: locked to viewport
        minHeight: '100vh',
        height: { lg: '100vh' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: { xs: '#ffffff', sm: 'linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)' },
        position: 'relative',
        overflow: { xs: 'auto', lg: 'hidden' },
        px: { xs: 0, sm: 2, md: 4 },
        py: { xs: 2, lg: 2 },
      }}
    >
      {/* Logo — only on desktop (lg+), floats top-right */}
      <Box
        sx={{
          display: { xs: 'none', lg: 'block' },
          position: 'absolute',
          top: 32,
          right: 48,
          zIndex: 1100,
        }}
      >
        <Box
          component="img"
          src="/lyftr-logo.jpg"
          alt="Lyftr AI"
          sx={{
            height: 96,
            width: 96,
            borderRadius: '50%',
            objectFit: 'contain',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            bgcolor: '#fff',
            transition: 'all 0.3s ease',
            '&:hover': { transform: 'scale(1.05)' },
          }}
          onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
            e.currentTarget.style.display = 'none'
          }}
        />
      </Box>

      {/* Card — full-width on mobile, constrained on desktop */}
      <Card
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          maxWidth: { xs: '100%', sm: 480, lg: 1100 },
          minHeight: { lg: 760 },
          maxHeight: { xs: 'none', lg: 'calc(100vh - 48px)' },
          borderRadius: { xs: 0, sm: 6, lg: 8 },
          background: { xs: '#ffffff', lg: 'rgba(255, 255, 255, 0.7)' },
          backdropFilter: { lg: 'blur(16px)' },
          border: { xs: 'none', sm: '1px solid rgba(255, 255, 255, 0.5)' },
          boxShadow: { xs: 'none', sm: '0 40px 80px rgba(0,0,0,0.06)' },
          overflow: 'hidden',
          // Mobile: full viewport width edge-to-edge, Desktop: auto margin
          mx: { xs: 0, sm: 'auto' },
          my: { xs: 0, sm: 4, lg: 0 },
        }}
      >
        {/* Left Side: Illustration — desktop only */}
        <Box
          sx={{
            display: { xs: 'none', lg: 'block' },
            width: '55%',
            flexShrink: 0,
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(180deg, #f0f7ff 0%, #ffffff 100%)',
          }}
        >
          <Box
            component="img"
            src="/auth-side-image.png"
            alt="Collaboration Illustration"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
              mixBlendMode: 'multiply',
              opacity: 0.95,
            }}
            onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
              e.currentTarget.style.display = 'none'
            }}
          />
        </Box>

        {/* Right Side: Form */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: { xs: 'flex-start', lg: 'center' },
            overflowY: { xs: 'visible', lg: 'auto' },
          }}
        >
          <CardContent
            sx={{
              p: { xs: 3, sm: 4, lg: 4 },
              pt: { xs: 4, sm: 5, lg: 4 },
              width: '100%',
            }}
          >
            {/* Mobile logo — inline at top of form */}
            <Box
              sx={{
                display: { xs: 'flex', lg: 'none' },
                justifyContent: 'center',
                mb: 3,
              }}
            >
              <Box
                component="img"
                src="/lyftr-logo.jpg"
                alt="Lyftr AI"
                sx={{
                  height: { xs: 90, sm: 100 },
                  width: { xs: 90, sm: 100 },
                  borderRadius: '50%',
                  objectFit: 'contain',
                }}
                onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </Box>

            <Collapse in={!!error} unmountOnExit>
              <Alert
                severity="error"
                onClose={() => setError('')}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  alignItems: 'flex-start',
                  '& .MuiAlert-message': { width: '100%' },
                }}
              >
                <AlertTitle sx={{ fontWeight: 700 }}>Sign Up Failed</AlertTitle>
                {error}
              </Alert>
            </Collapse>

            <form onSubmit={handleSubmit}>
              <Typography
                variant="h5"
                fontWeight="900"
                align="center"
                sx={{
                  mb: { xs: 2, lg: 4 },
                  fontSize: { xs: '1.35rem', sm: '1.45rem', lg: '1.55rem' },
                  color: 'text.primary',
                  letterSpacing: -0.5,
                }}
              >
                Create your business account
              </Typography>

              <TextField
                fullWidth
                id="name"
                name="name"
                label="Business Name"
                value={formData.name}
                onChange={handleChange}
                required
                autoComplete="name"
                autoFocus={window.innerWidth >= 1024}
                error={!!fieldErrors.name}
                helperText={fieldErrors.name || ' '}
                sx={{ mb: { xs: 0, lg: 0.5 } }}
                InputProps={{ sx: { borderRadius: 3, fontSize: '1rem' } }}
                inputProps={{ enterKeyHint: 'next' }}
              />

              <TextField
                fullWidth
                id="email"
                name="email"
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
                error={!!fieldErrors.email}
                helperText={fieldErrors.email || ' '}
                sx={{ mb: { xs: 0, lg: 0.5 } }}
                InputProps={{ sx: { borderRadius: 3, fontSize: '1rem' } }}
                inputProps={{ inputMode: 'email', enterKeyHint: 'next' }}
              />

              <PasswordField
                fullWidth
                id="password"
                name="password"
                label="Password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
                error={!!fieldErrors.password}
                helperText={fieldErrors.password || ' '}
                sx={{ mb: { xs: 0, lg: 0.5 } }}
                InputProps={{ sx: { borderRadius: 3, fontSize: '1rem' } }}
                inputProps={{ enterKeyHint: 'next' }}
              />

              <PasswordField
                fullWidth
                id="confirmPassword"
                name="confirmPassword"
                label="Confirm Password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                autoComplete="new-password"
                error={!!fieldErrors.confirmPassword}
                helperText={fieldErrors.confirmPassword || ' '}
                sx={{ mb: { xs: 0, lg: 0.5 } }}
                InputProps={{ sx: { borderRadius: 3, fontSize: '1rem' } }}
                inputProps={{ enterKeyHint: 'done' }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    name="agreeToTerms"
                    checked={formData.agreeToTerms}
                    onChange={handleChange}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{' '}
                    <Link href="https://lyftr.ai/terms" target="_blank" color="primary">
                      Terms
                    </Link>{' '}
                    &{' '}
                    <Link href="https://lyftr.ai/privacy-policy" target="_blank" color="primary">
                      Privacy Policy
                    </Link>
                  </Typography>
                }
                sx={{ mb: { xs: 2, lg: 3 } }}
              />

              <Stack spacing={2}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  sx={{
                    py: { xs: 1.75, lg: 2 },
                    fontSize: '1rem',
                    fontWeight: '800',
                    borderRadius: 3.5,
                    minHeight: 48, // ≥48px touch target
                    background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)',
                    boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 32px rgba(59, 130, 246, 0.4)',
                    },
                  }}
                >
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </Button>

                <Divider>
                  <Typography variant="body2" color="text.secondary" fontWeight="700" sx={{ opacity: 0.6 }}>
                    OR
                  </Typography>
                </Divider>

                <Typography variant="body2" color="text.secondary" align="center">
                  Already have an account?{' '}
                  <Link component={RouterLink} to="/login" color="primary" fontWeight="700">
                    Login
                  </Link>
                </Typography>
              </Stack>
            </form>
          </CardContent>
        </Box>
      </Card>
    </Box>
  )
}

export default Signup

